import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  ViewEncapsulation
} from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import {
  NzUploadChangeParam,
  NzUploadFile,
  NzUploadModule,
  NzUploadXHRArgs
} from 'ng-zorro-antd/upload';
import { TranslatePipe } from '@ngx-translate/core';
import { UploadListComponent } from './upload-list/upload-list.component';
import { Subscription } from 'rxjs';
import { HttpEventType } from '@angular/common/http';
import { DocumentHttpService } from '@shared/data';
import { NotificationService } from '@shared/services';
import { DocumentType } from '@moxi.gmbh/moxi-typescriptmodels';
import { downloadBlob } from '@shared/utils';
import { FormatFileSizePipe } from './format-file-size.pipe';

@Component({
  selector: 'moxi-ui-upload',
  imports: [
    UploadListComponent,
    FormatFileSizePipe,

    TranslatePipe,

    // Ant Design
    NzUploadModule,
    NzIconModule,
    NzButtonModule
  ],
  templateUrl: './upload.component.html',
  styleUrl: './upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class UploadComponent {
  private readonly documentService = inject(DocumentHttpService);
  private readonly notificationService = inject(NotificationService);

  files = input.required<NzUploadFile[]>();
  documentType = input.required<DocumentType>();
  accountId = input.required<string>();

  uploadType = input<'drag' | undefined>('drag');
  fileTypes = input('application/pdf');
  sizeLimit = input(1024 * 1024 * 5); // 5MB
  allowMultiple = input(false);
  isDisabled = input(true);

  uploadedFiles = output<NzUploadFile[]>();
  removeFile = output<NzUploadFile>();

  fileList = linkedSignal<NzUploadFile[]>(() => this.files());

  allowedFileTypes = computed(() => this.formatFileTypes());
  uploadVisibility = computed(() => {
    return this.fileList().length ? undefined : this.uploadType();
  });

  uploadRequest = (item: NzUploadXHRArgs): Subscription => {
    const fileName = item.file.name;

    const req = this.documentService.upload(
      item.file as unknown as Blob,
      fileName,
      this.accountId(),
      this.documentType()
    );

    return new Subscription(() =>
      req.subscribe({
        next: event => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            const percent = Math.round((event.loaded / event.total) * 100);

            item.onProgress({ percent }, item.file);
          } else if (event.type === HttpEventType.Response) {
            item.onSuccess(event.body, item.file, event);

            this.updateFileInFileList(fileName, event.body.id);
          }
        },
        error: err => {
          item.onError(err, item.file);

          this.notificationService.error('shared.upload.error');
        }
      })
    );
  };

  onHandleChange({ file, fileList }: NzUploadChangeParam): void {
    this.fileList.set([...fileList]);

    if (file.status === 'error') {
      this.fileList.update(files =>
        files.filter(item => item.uid !== file.uid)
      );
    }
  }

  onRemoveFile(file: NzUploadFile): void {
    this.documentService.delete(file.uid).subscribe({
      next: () => {
        this.fileList.update(files =>
          files.filter(item => item.uid !== file.uid)
        );

        this.removeFile.emit(file);
      },
      error: () => this.notificationService.error('shared.upload.error')
    });
  }

  onDownloadFile(file: NzUploadFile): void {
    this.documentService.download(file.uid).subscribe({
      next: response => {
        const blob = new Blob([response], { type: 'application/pdf' });
        const filename = file.name || `document-${file.uid}.pdf`;

        downloadBlob(blob, filename);
      }
    });
  }

  private updateFileInFileList(fileName: string, documentId: string): void {
    this.fileList.update(files =>
      files.map(file => {
        if (file.name === fileName) {
          file.uid = documentId;
          file.url = documentId;
        }

        return file;
      })
    );

    this.uploadedFiles.emit(this.fileList());
  }

  private formatFileTypes(): string {
    return this.fileTypes()
      .split(',')
      .map(mimeType => mimeType.trim().split('/').pop() || '')
      .filter(Boolean)
      .join(', ');
  }
}
